import { useEffect, useMemo, useState } from "react";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import {
  CustomMetadata,
  CustomMetadataOperators,
  CUSTOM_METADATA_NUMBER_OPERATORS,
  QueryOperator,
} from "types";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { CustomNameNumberIcon } from "assets/icons/CustomMetadataIcons";
import { useCustomMetadataModal } from "hooks/useCustomMetadataModal";
import BaseQueryModal from "./BaseQueryModal";
import { useNumberQueryModal } from "hooks/UseQueryModal/useNumberQueryModal";
import useVqlParams from "hooks/useVqlParams";
import {
  getCustomMetadataQueryValue,
  getExistingCustomMetadataQuery,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";

interface NumberModalProps {
  field: CustomMetadata;
  editQueryId?: string;
}

export default function NumberModal({ editQueryId, field }: NumberModalProps) {
  const { modalRef, oldQuery, handleSubmission, fieldName } =
    useCustomMetadataModal({ field, editQueryId });

  const { vqlQueries } = useVqlParams();

  const queryOperator = oldQuery
    ? (convertAPIOperator(
        oldQuery.custom_metadata.op,
      ) as CustomMetadataOperators)
    : null;

  const [selectedOp, setSelectedOp] = useState<CustomMetadataOperators | null>(
    queryOperator,
  );

  const existingQuery = useMemo(() => {
    return getExistingCustomMetadataQuery(
      vqlQueries,
      field.field_id,
      selectedOp as CustomMetadataOperators,
    );
  }, [selectedOp, field.field_id, vqlQueries]);

  const previousQueryOp = useMemo(() => {
    if (oldQuery) {
      return convertAPIOperator(
        oldQuery.custom_metadata.op,
      ) as CustomMetadataOperators;
    }
    if (existingQuery && isVqlQuery(existingQuery, "custom_metadata")) {
      return convertAPIOperator(
        existingQuery.custom_metadata.op,
      ) as CustomMetadataOperators;
    }
    return null;
  }, [oldQuery, existingQuery]);

  const previousValue = useMemo(() => {
    const value = getCustomMetadataQueryValue(
      editQueryId,
      oldQuery,
      selectedOp as QueryOperator,
      previousQueryOp as QueryOperator,
      existingQuery,
    );
    return value ? String(value) : "";
  }, [editQueryId, oldQuery, selectedOp, previousQueryOp, existingQuery]);

  useEffect(() => {
    setSearchText(previousValue ?? "");
  }, [selectedOp, previousValue]);

  const [searchText, setSearchText] = useState(previousValue);

  const operatorsWithDefault = [
    { value: null, label: "Choose Type" },
    ...CUSTOM_METADATA_NUMBER_OPERATORS,
  ];

  const { isSubmitDisabled } = useNumberQueryModal({
    inputValue: searchText ? Number(searchText) : null,
    previousValue: previousValue ? Number(previousValue) : null,
    selectedOperator: selectedOp as QueryOperator,
    initialOperator: previousQueryOp as QueryOperator,
  });

  const handleSubmit = () => {
    if (isSubmitDisabled || !selectedOp) return;
    handleSubmission(selectedOp, searchText.trim());
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  const handleOptionSelection = (operator: CustomMetadataOperators | null) => {
    if (operator === selectedOp) return;
    setSelectedOp(operator);
  };

  const operators = {
    options: operatorsWithDefault,
    selected: selectedOp,
    onSelect: handleOptionSelection,
  };

  return (
    <BaseQueryModal<CustomMetadataOperators | null>
      title={fieldName}
      keepOpenOnLoading
      modalRef={modalRef}
      icon={<CustomNameNumberIcon />}
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={handleSubmit}
      operators={operators}
    >
      <QueryModalInput
        numberOnly
        searchText={searchText}
        placeholder="Number Input"
        onInputChange={handleInputChange}
      />
    </BaseQueryModal>
  );
}
